import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { authService, authValidation } from "../authService";

/**
 * Custom hook for managing signup form state and logic
 */
export const useSignUpForm = () => {
  const { t } = useTranslation("auth");

  // Form state
  const [formData, setFormData] = useState({
    username: "",
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    otp: "",
    profileImage: null,
  });

  // Form validation and error states
  const [formErrors, setFormErrors] = useState({});
  const [fieldValidation, setFieldValidation] = useState({
    username: { isValid: null, message: "" },
    email: { isValid: null, message: "" },
  });

  // Loading and step states
  const [isLoading, setIsLoading] = useState({
    checkingUsername: false,
    checkingEmail: false,
    sendingOtp: false,
    submitting: false,
  });

  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Memoized toggle functions
  const toggleShowPassword = useCallback(
    () => setShowPassword((prev) => !prev),
    [],
  );
  const toggleShowConfirmPassword = useCallback(
    () => setShowConfirmPassword((prev) => !prev),
    [],
  );

  // Handle form input changes
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        const newFormData = { ...prev, [name]: value };

        // Real-time password and confirmation validation
        if (name === "confirmPassword" || name === "password") {
          const password = name === "password" ? value : prev.password;
          const confirmPassword =
            name === "confirmPassword" ? value : prev.confirmPassword;

          setFormErrors((prevErrors) => {
            const newErrors = { ...prevErrors };

            // Password strength validation
            if (name === "password" && value) {
              const passwordValidation = authValidation.validatePassword(value);
              if (!passwordValidation.isValid) {
                newErrors.password = passwordValidation.errors[0];
              } else {
                delete newErrors.password;
              }
            } else if (name === "password" && !value) {
              delete newErrors.password;
            }

            // Password confirmation validation
            if (confirmPassword && password && password !== confirmPassword) {
              newErrors.confirmPassword = "Passwords do not match";
            } else if (
              confirmPassword &&
              password &&
              password === confirmPassword
            ) {
              delete newErrors.confirmPassword;
            } else if (!confirmPassword) {
              delete newErrors.confirmPassword;
            }

            return newErrors;
          });
        } else {
          // Clear errors for other fields when user starts typing
          setFormErrors((prev) => {
            if (prev[name]) {
              return { ...prev, [name]: "" };
            }
            return prev;
          });
        }

        return newFormData;
      });

      // Clear messages if they exist
      if (submitError) setSubmitError("");
      if (successMessage) setSuccessMessage("");
    },
    [submitError, successMessage],
  );

  // Check username availability
  const checkUsernameAvailability = useCallback(
    async (username) => {
      if (!username || username.length < 3) {
        setFieldValidation((prev) => ({
          ...prev,
          username: { isValid: null, message: "" },
        }));
        return;
      }

      const validation = authValidation.validateUsername(username);
      if (!validation.isValid) {
        setFieldValidation((prev) => ({
          ...prev,
          username: { isValid: false, message: validation.errors[0] },
        }));
        return;
      }

      setIsLoading((prev) => ({ ...prev, checkingUsername: true }));

      try {
        const result = await authService.checkUsername(username);

        if (result.success) {
          const exists = result.data?.exists || result.data?.data?.exists;
          setFieldValidation((prev) => ({
            ...prev,
            username: {
              isValid: !exists,
              message: exists
                ? t("validation.usernameTaken")
                : t("validation.usernameAvailable"),
            },
          }));
        } else {
          setFieldValidation((prev) => ({
            ...prev,
            username: { isValid: null, message: "Error checking username" },
          }));
        }
      } catch (error) {
        console.error("Username check error:", error);
        setFieldValidation((prev) => ({
          ...prev,
          username: {
            isValid: null,
            message: "Error checking username availability",
          },
        }));
      } finally {
        setIsLoading((prev) => ({ ...prev, checkingUsername: false }));
      }
    },
    [t],
  );

  // Check email availability
  const checkEmailAvailability = useCallback(
    async (email) => {
      if (!email || !authValidation.isValidEmail(email)) {
        setFieldValidation((prev) => ({
          ...prev,
          email: { isValid: null, message: "" },
        }));
        return;
      }

      setIsLoading((prev) => ({ ...prev, checkingEmail: true }));

      try {
        const result = await authService.checkEmail(email);

        if (result.success) {
          const exists = result.data?.exists || result.data?.data?.exists;
          setFieldValidation((prev) => ({
            ...prev,
            email: {
              isValid: !exists,
              message: exists
                ? t("validation.emailTaken")
                : t("validation.emailAvailable"),
            },
          }));
        } else {
          setFieldValidation((prev) => ({
            ...prev,
            email: { isValid: null, message: "Error checking email" },
          }));
        }
      } catch (error) {
        console.error("Email check error:", error);
        setFieldValidation((prev) => ({
          ...prev,
          email: {
            isValid: null,
            message: "Error checking email availability",
          },
        }));
      } finally {
        setIsLoading((prev) => ({ ...prev, checkingEmail: false }));
      }
    },
    [t],
  );

  return {
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    fieldValidation,
    isLoading,
    setIsLoading,
    submitError,
    setSubmitError,
    successMessage,
    setSuccessMessage,
    otpSent,
    setOtpSent,
    showPassword,
    showConfirmPassword,
    toggleShowPassword,
    toggleShowConfirmPassword,
    handleChange,
    checkUsernameAvailability,
    checkEmailAvailability,
  };
};
